/**
 * The chat workstream's API surface — the `/chat` SSE stream + the session REST endpoints
 * (editor-chat.md §2.3, §2.4; ADR 007).
 *
 * The chat endpoint is REST-only app-shell plumbing (the same posture ADR 008 takes for
 * `health/*` etc.) — these go through `callEndpoint` for the session CRUD, and through a
 * **bespoke streaming fetch** for `POST /api/v1/chat` (an SSE stream).
 *
 * WHY a bespoke fetch for the stream, not the shared `client.ts`: `client.ts` is built around
 * "fetch → parse one JSON body → unwrap". The `/chat` response is a `text/event-stream` — a
 * sequence of `data:` frames read incrementally. `EventSource` can't carry the
 * `Authorization: Bearer` loopback token (M4), so this uses `fetch` + a `ReadableStream`
 * reader, attaching the same loopback token + `X-Query-Id` correlation id the shared client
 * attaches (conventions.md §4). It is the *one* sanctioned second fetch path, scoped to the
 * streaming case, and it never logs the token (conventions.md §4).
 *
 * snake↔camel: each parsed SSE frame is mapped snake→camel via `keysToCamel` — the chat
 * surface's boundary mapping, the same role `mappers.ts` plays for the REST client
 * (conventions.md §2.3).
 */

import { callEndpoint } from '@/api';
import { keysToCamel } from '@/api/mappers';
import { getLogger } from '@/api';
import type {
  ChatSession,
  ChatStreamEvent,
  ChatTurnRequest,
  SaveAsNoteRequest,
  SaveSessionAsNoteResult,
  SessionListResponse,
} from './chatTypes';

const log = getLogger('chat.api');

// ---------------------------------------------------------------------------
// Platform seam — the loopback token + base URL (mirrors src/api/client.ts)
// ---------------------------------------------------------------------------

interface AletheiaGlobals {
  loopbackToken?: string;
  apiBaseUrl?: string;
}

/** Read the platform globals for the chat stream.
 *
 * `__ALETHEIA_CHAT__` is the desktop's CHAT-ONLY direct-Azure config (apiBaseUrl + token),
 * set by `configureDesktopDirectBackend` so the SSE stream bypasses the buffering SWA proxy.
 * It is deliberately separate from the global `__ALETHEIA__` that the rest of the app reads —
 * non-chat calls stay on the same-origin proxy, so a direct-Azure issue can't break the whole
 * app. Falls back to `__ALETHEIA__`, then to `{}` (web/dev → same-origin proxy). */
function platformGlobals(): AletheiaGlobals {
  if (typeof window === 'undefined') return {};
  const w = window as {
    __ALETHEIA_CHAT__?: AletheiaGlobals;
    __ALETHEIA__?: AletheiaGlobals;
  };
  return w.__ALETHEIA_CHAT__ ?? w.__ALETHEIA__ ?? {};
}

/** Mint a per-request correlation id — sent as `X-Query-Id`, threaded through the backend logs. */
function newQueryId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `qid-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

// ---------------------------------------------------------------------------
// The /chat SSE stream
// ---------------------------------------------------------------------------

/** What `streamChatTurn` returns — the async iterable of events + the turn's correlation id. */
export interface ChatStreamHandle {
  /** The turn's `X-Query-Id` — quotable, joins the backend logs (conventions.md §4). */
  readonly turnId: string;
  /** An async iterable of the turn's `ChatStreamEvent`s — ends after the terminal event. */
  events: AsyncIterable<ChatStreamEvent>;
  /**
   * Abort this turn's stream (perf D5): aborts the fetch and cancels the SSE reader, so the
   * connection tears down instead of lingering. Idempotent, safe at any time — the backend
   * persists the user message at turn start and cancels the model call on disconnect, so an
   * abort is cheap server-side. After cancel, the `events` iterable winds down (it may end
   * cleanly or throw an `AbortError` depending on where the read was — callers that cancelled
   * deliberately should discard either outcome).
   */
  cancel: () => void;
}

/**
 * How long the stream may go with NO raw bytes before the frontend declares it stalled
 * (perf D6). The backend emits `: keepalive` SSE comment frames every ~15s — those never
 * surface as parsed events, but their *bytes* reset this watchdog — so 60s of true byte
 * silence means the connection is dead (a dropped socket the OS hasn't noticed), not a slow
 * model call.
 */
export const STREAM_STALL_TIMEOUT_MS = 60_000;

/** Thrown out of the `events` iterable when the stall watchdog fires (perf D6). The consumer
 *  (`useChatStream`) surfaces `message` through its existing synthesized-terminal-error path. */
export class ChatStreamStalledError extends Error {
  constructor() {
    super('The stream stalled — check your connection and retry.');
    this.name = 'ChatStreamStalledError';
  }
}

/**
 * Parse one SSE `data:` block into a `ChatStreamEvent`. An SSE frame is `data: <json>\n\n`;
 * this is handed the `<json>` payload. The parsed object is mapped snake→camel (the wire is
 * snake_case — conventions.md §2.3). Returns `null` for an un-parseable frame (logged, then
 * skipped — a single bad frame must not abort the stream; the turn's terminal event still
 * arrives).
 */
function parseEventFrame(jsonPayload: string): ChatStreamEvent | null {
  try {
    const raw = JSON.parse(jsonPayload) as unknown;
    return keysToCamel<ChatStreamEvent>(raw);
  } catch (err) {
    log.warn('chat_sse_frame_parse_failed', { error: String(err) });
    return null;
  }
}

/**
 * Open the `POST /api/v1/chat` SSE stream for one turn (editor-chat.md §2.3).
 *
 * Attaches the loopback token + a fresh `X-Query-Id` (the turn's correlation id), POSTs the
 * `ChatTurnRequest` as JSON, and reads the `text/event-stream` response incrementally —
 * yielding one `ChatStreamEvent` per `data:` frame. The async iterable ends when the stream
 * closes (the backend always ends a turn with a terminal `done` / `error` event — conventions
 * §3, §7 — so the last yielded event is terminal).
 *
 * An auth / transport failure before the stream opens throws — the chat surface catches it
 * and renders the graceful-failure state (it never gets a terminal event in that case, so the
 * surface synthesizes one). Once the stream is open, *every* outcome is an event in the
 * stream.
 *
 * @param request - The turn request — `userMessage` + optional `sessionId`.
 * @param options - `signal`: an external `AbortSignal` chained onto the stream's own
 *   controller, so a caller that owns an `AbortController` *before* the stream opens (the
 *   `useChatStream` hook — perf D5) can abort even during the connection-opening `await`.
 * @returns A `ChatStreamHandle` — the turn id + the async iterable of events + `cancel`.
 */
export async function streamChatTurn(
  request: ChatTurnRequest,
  options: { signal?: AbortSignal } = {},
): Promise<ChatStreamHandle> {
  const turnId = newQueryId();
  const token = platformGlobals().loopbackToken;
  const baseUrl = platformGlobals().apiBaseUrl ?? '';

  // D5: the turn's AbortController — aborts the fetch AND (via readSseStream's abort
  // listener) cancels the reader. Chained to the caller's external signal, if any.
  const controller = new AbortController();
  if (options.signal !== undefined) {
    if (options.signal.aborted) controller.abort();
    else options.signal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
    'X-Query-Id': turnId,
  };
  // M4: the loopback token on the streaming call too — never logged (conventions.md §4).
  if (token !== undefined) headers['Authorization'] = `Bearer ${token}`;
  else log.warn('chat_sse_no_loopback_token', {});

  // The wire body is snake_case — `sessionId` → `session_id`, `userMessage` → `user_message`.
  // facelift-v3: the soft-routing hints (`workflow` / `scope`) are included ONLY when set.
  // Omitting them when null keeps a default turn byte-identical to the pre-routing wire
  // format, so an older backend (whose ChatTurnRequest is extra="forbid") still accepts
  // it — eliminating the deploy-ordering hazard where the frontend ships before the backend.
  const payload: Record<string, unknown> = {
    session_id: request.sessionId ?? null,
    user_message: request.userMessage,
  };
  if (request.workflow != null && request.workflow !== '') payload.workflow = request.workflow;
  if (request.scope != null && request.scope !== '') payload.scope = request.scope;
  // Only send `mode` when 'deep' — 'fast' is the backend default, so omitting it keeps a
  // default turn's wire byte-identical (and an older backend that predates the field ignores
  // an absent field cleanly).
  if (request.mode === 'deep') payload.mode = 'deep';
  const body = JSON.stringify(payload);

  const response = await fetch(`${baseUrl}/api/v1/chat`, {
    method: 'POST',
    headers,
    body,
    credentials: 'omit',
    signal: controller.signal,
  });

  if (!response.ok || response.body === null) {
    // The stream never opened — an auth failure (401/403) or a transport fault. Throw; the
    // chat surface catches it and renders the graceful-failure state. Abort first so a
    // non-ok response body doesn't pin the connection.
    controller.abort();
    log.error('chat_sse_open_failed', { status: response.status, turnId });
    throw new Error(
      response.status === 401 || response.status === 403
        ? 'The chat request was not authorized.'
        : 'The chat backend is unreachable.',
    );
  }

  const events = readSseStream(response.body, turnId, controller);
  return { turnId, events, cancel: () => controller.abort() };
}

/**
 * Read a `ReadableStream` of SSE bytes and yield one `ChatStreamEvent` per `data:` frame.
 *
 * SSE framing: events are separated by a blank line (`\n\n`); within an event, lines prefixed
 * `data:` carry the payload. This decodes the byte stream, buffers across chunk boundaries
 * (a frame can straddle two reads), and parses each complete `data:` block. A frame that
 * fails to parse is logged and skipped — one bad frame must not kill the turn.
 *
 * Comment frames (lines starting `:` — the backend's ~15s keepalives) carry no `data:` lines,
 * so they yield nothing — but their bytes DO reset the stall watchdog below (perf D6): the
 * watchdog runs on raw read activity, not parsed events.
 *
 * Cancellation (perf D5): when `controller` aborts, the abort listener cancels the reader so
 * a pending `read()` resolves and the generator winds down promptly. The stall watchdog
 * (perf D6) aborts the same controller after `STREAM_STALL_TIMEOUT_MS` of byte silence and
 * throws `ChatStreamStalledError` out of the iterable.
 */
async function* readSseStream(
  stream: ReadableStream<Uint8Array>,
  turnId: string,
  controller: AbortController,
): AsyncGenerator<ChatStreamEvent> {
  const reader = stream.getReader();
  // D5: an abort (session switch / unmount / user cancel / stall) must actively cancel the
  // reader — aborting the fetch alone can leave a pending read() hanging in some runtimes.
  const onAbort = (): void => {
    void reader.cancel().catch(() => undefined);
  };
  controller.signal.addEventListener('abort', onAbort, { once: true });
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    for (;;) {
      // D6: race each read against a fresh stall timer — ANY bytes (data frames or keepalive
      // comment frames) reset it, because each successful read mints a new timer.
      const { done, value } = await readWithStallTimeout(reader, STREAM_STALL_TIMEOUT_MS);
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // Process every complete event (terminated by a blank line) in the buffer.
      let sep = buffer.indexOf('\n\n');
      while (sep !== -1) {
        const rawEvent = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        // An SSE event can have multiple lines; collect the `data:` lines' payloads.
        const dataLines = rawEvent
          .split('\n')
          .filter((line) => line.startsWith('data:'))
          .map((line) => line.slice(5).trimStart());
        if (dataLines.length > 0) {
          const event = parseEventFrame(dataLines.join('\n'));
          if (event !== null) yield event;
        }
        sep = buffer.indexOf('\n\n');
      }
    }
    // Flush any trailing partial frame (a well-behaved backend ends on `\n\n`, but be robust).
    const trailing = buffer.trim();
    if (trailing.startsWith('data:')) {
      const event = parseEventFrame(trailing.slice(5).trimStart());
      if (event !== null) yield event;
    }
  } catch (err) {
    if (err instanceof ChatStreamStalledError) {
      // D6: the watchdog fired — tear down the dead connection (the backend cancels the model
      // call on disconnect), then let the error propagate to the consumer, which synthesizes
      // the terminal error state from `err.message`.
      log.warn('chat_sse_stream_stalled', { turnId, timeoutMs: STREAM_STALL_TIMEOUT_MS });
      controller.abort();
    }
    throw err;
  } finally {
    controller.signal.removeEventListener('abort', onAbort);
    reader.releaseLock();
    log.debug('chat_sse_stream_closed', { turnId });
  }
}

/**
 * One watchdogged read (perf D6): resolve with the reader's next chunk, or reject with
 * `ChatStreamStalledError` if no bytes arrive within `timeoutMs`. The underlying `read()`
 * promise keeps its handlers attached either way, so a late settle after the timer fired is
 * a no-op (never an unhandled rejection).
 */
function readWithStallTimeout(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  timeoutMs: number,
): Promise<ReadableStreamReadResult<Uint8Array>> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new ChatStreamStalledError()), timeoutMs);
    reader.read().then(
      (result) => {
        clearTimeout(timer);
        resolve(result);
      },
      (err: unknown) => {
        clearTimeout(timer);
        reject(err instanceof Error ? err : new Error(String(err)));
      },
    );
  });
}

// ---------------------------------------------------------------------------
// The session REST endpoints (editor-chat.md §2.4) — plain JSON, via callEndpoint
// ---------------------------------------------------------------------------

/** `GET /api/v1/chat/sessions` — a page of sessions, most-recently-updated first. */
export const listChatSessions = (
  limit = 50,
  offset = 0,
): Promise<SessionListResponse> =>
  callEndpoint('GET', 'chat/sessions', { query: { limit, offset } });

/** `GET /api/v1/chat/sessions/{id}` — one session with its full message history. */
export const getChatSession = (sessionId: string): Promise<ChatSession> =>
  callEndpoint('GET', `chat/sessions/${sessionId}`);

/** `POST /api/v1/chat/sessions/{id}/rename` — set a session's user-editable title. */
export const renameChatSession = (
  sessionId: string,
  title: string,
): Promise<ChatSession> =>
  callEndpoint('POST', `chat/sessions/${sessionId}/rename`, { body: { title } });

/** `DELETE /api/v1/chat/sessions` — clear all chat history (delete every session). */
export const clearChatHistory = (): Promise<{ cleared: number }> =>
  callEndpoint('DELETE', 'chat/sessions');

/** `POST /api/v1/chat/sessions/{id}/save_as_note` — promote a session to a `source='chat'` note. */
export const saveChatSessionAsNote = (
  sessionId: string,
  request: SaveAsNoteRequest = {},
): Promise<SaveSessionAsNoteResult> =>
  callEndpoint('POST', `chat/sessions/${sessionId}/save_as_note`, {
    body: { notebook_id: request.notebookId ?? null },
  });
